<template>
  <responsive-drawer-layout>
    <template v-slot:content>
      <v-data-table
        v-if="mdAndUp"
        item-key="id"
        fixed-header
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        :headers="headers"
        :items="reportsFiltered"
        :items-per-page="10"
        :item-class="getRowClass"
      >
        <template v-slot:[`item.status`]="{ item }">
          {{ $t('module.wagenbuch.status.' + item.status) }}
        </template>
        <template v-slot:[`item.beschrieb`]="{ item }">
          {{ item.beschrieb }}
        </template>
        <template v-slot:[`item.clientdate`]="{ item }">
          {{ mx_formatIsoDateStringAsLocaleDateTime(item.clientdate) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          {{ $t(`module.wagenbuch.status.${item.status}`) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn v-if="item.pdf" @click="openReportDialog(item)" icon>
            <v-icon>mdi-download</v-icon>
          </v-btn>

          <v-badge v-if="item.comments && itemHasUnreadComments(item)" :color="badgeColor" dot overlap>
            <v-btn @click.stop="expanded = expanded.length === 0 ? [item] : []" icon>
              <v-icon>mdi-chat</v-icon>
            </v-btn>
          </v-badge>
          <v-btn v-else-if="item.comments" @click.stop="expanded = expanded.length === 0 ? [item] : []" icon>
            <v-icon>mdi-chat</v-icon>
          </v-btn>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="background darken-2">
            <div class="pa-3">
              <div v-for="(comment, ind) in item.comments" :key="ind">
                {{ mx_formatIsoDateStringAsLocaleDateTime(comment.clientdate) }}, {{ comment.username }}:
                {{ comment.kommentar }}
              </div>
            </div>
          </td>
        </template>
      </v-data-table>

      <div v-else class="d-flex flex-column inherit-height" style="overflow-y: scroll">
        <v-list three-line>
          <template v-for="(item, index) in reportsFiltered">
            <v-divider :key="index"></v-divider>
            <v-list-item :key="item.id" @click="openReportDialog(item)" :class="getRowClass(item)">
              <v-list-item-content>
                <v-list-item-title v-if="item.typ === 'ereignis'">{{ item.ereignisName }}</v-list-item-title>
                <v-list-item-title v-else>{{ item.klassifizierung }}</v-list-item-title>
                <div class="caption pb-1">
                  {{ mx_formatIsoDateStringAsLocaleDateTime(item.clientdate) }},
                  <span v-if="showReporter">{{ item.username }},</span>
                  {{ $t(`module.wagenbuch.fz`) }} {{ item.name }}
                </div>
                <div v-if="item.typ !== 'ereignis'" class="caption pb-3">{{ item.beschrieb }}</div>

                <v-list-item-subtitle v.if="xs">
                  <div class="primary--text">
                    {{ $t(`module.wagenbuch.status.${item.status}`) }}
                  </div>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="item.pdf">
                <v-list-item-action-text v-text="item.action"></v-list-item-action-text>
                <v-btn @click="openReportDialog(item)" icon>
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-chip v-if="!xs" :class="`${theme.elements.btnColor} ${theme.elements.btnTextClz}`" class="ml-5" small
                  >{{ $t(`module.wagenbuch.status.${item.status}`) }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </div>

      <v-dialog v-model="reportDialog" fullscreen>
        <v-card v-if="item" style="height: 100vh">
          <v-toolbar>
            <v-dialog v-model="commentDialog" width="fit-content" max-width="600px" v-if="!disableAddComment">
              <template v-slot:activator="{ on, attrs }">
                <v-btn text v-bind="attrs" v-on="on">
                  <v-icon left> mdi-plus</v-icon>
                  {{ $t('module.wagenbuch.comment.add') }}
                </v-btn>
              </template>
              <v-card class="comment-dialog">
                <v-card-title>{{ $t('module.wagenbuch.comment') }}:</v-card-title>

                <v-card-text>
                  <v-text-field v-model="comment" />
                  <div>{{ $t('module.wagenbuch.comment.info') }}</div>
                </v-card-text>
                <v-card-actions style="justify-content: flex-end">
                  <v-btn text @click="commentDialog = false">{{ $t('cancel') }}</v-btn>
                  <v-btn text color="green" :disabled="!comment" @click="addComment(item)">
                    {{ $t('button.save') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-spacer></v-spacer>
            <v-btn @click.stop="closeReportDialog" icon>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <pdf-js
            :url="`${config.apiUrl}/medias/wagenbuch/report/${item.pdf}/${item.checksum}`"
            :allowFullScreen="false"
            :hide-pdf-open-icon="hidePdfOpenIcon"
          ></pdf-js>
        </v-card>
      </v-dialog>
      <!--      </div>-->
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import PdfJs from '@/components/pdf/PdfJs';
import index from './index';
import config from '@/common/config';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import wagenbuchService from '@/modules/wagenbuch/wagenbuchService';
import vehicleMixin from '@/mixins/vehicleMixin';

export default {
  name: 'Wagenbuch',
  mixins: [common, configMixin, dateTimeMixin, vehicleMixin],
  components: {
    ResponsiveDrawerLayout,
    PdfJs,
  },
  data() {
    return {
      config,
      expanded: [],
      comment: undefined,
      commentDialog: false,
      reportDialog: false,
      item: null, // selected item,
      singleExpand: false,
    };
  },
  computed: {
    ...mapState({
      isToolbarsHidden: (state) => state.isToolbarsHidden,
    }),
    ...mapState('wagenbuch', ['reports', 'comments', 'selectedFzId']),
    filterReportId() {
      return Number(this.$route.query.reportId);
    },
    showReporter() {
      return !this.moduleConfig.hideReporter || this.moduleConfig.hideReporter === false;
    },
    headers() {
      const headers = [];
      if (this.selectedTab === 'ereignis') {
        headers.push({ text: this.$t('module.wagenbuch.report.date'), value: 'clientdate' });
        if (!this.hideFahrzeug) {
          headers.push({ text: this.$t('module.wagenbuch.fz'), value: 'name' });
        }
        headers.push({ text: this.$t('module.wagenbuch.ereignis'), value: 'ereignisName' });
        if (this.showReporter && !this.hideErfasser) headers.push({ text: this.$t('module.wagenbuch.report.creator'), value: 'username' });
        headers.push({ text: this.$t('module.wagenbuch.report.status'), value: 'status' });
        headers.push({ text: '', value: 'actions', sortable: false });
        headers.push({ text: '', value: '' });
      } else {
        headers.push({ text: this.$t('module.wagenbuch.report.date'), value: 'clientdate' });
        if (!this.hideFahrzeug) {
          headers.push({ text: this.$t('module.wagenbuch.fz'), value: 'name' });
        }
        headers.push({ text: this.$t('module.wagenbuch.typ.klassifizierung'), value: 'klassifizierung' });
        if (!this.hideBeschrieb) {
          headers.push({ text: this.$t('module.wagenbuch.beschrieb'), value: 'beschrieb' });
        }

        if (this.showReporter && !this.hideErfasser) headers.push({ text: this.$t('module.wagenbuch.report.creator'), value: 'username' });
        headers.push({ text: this.$t('module.wagenbuch.report.status'), value: 'status' });
        headers.push({ text: '', value: 'actions', sortable: false });
        headers.push({ text: '', value: '' });
      }
      return headers;
    },
    selectedTab() {
      return this.$route.query.tab;
    },
    reportsFiltered() {
      // If a report has been found within the global search, this report is looked for
      if (this.filterReportId) {
        return this.reports.filter((r) => r.id === this.filterReportId);
      }

      if (!this.vehicle) return wagenbuchService.filterReportsByTab(this.reports, this.selectedTab);

      const filtered = wagenbuchService.filterReports(this.reports, this.vehicle && this.vehicle.fzName, this.selectedTab);

      return wagenbuchService.sortReportsByReportingDate(filtered);
    },
    moduleConfig() {
      return this.getModuleConfig(index.moduleType, this.$route.params.instance);
    },
    disableAddComment() {
      return this.moduleConfig.disableAddComment;
    },
    hidePdfOpenIcon() {
      return this.moduleConfig.hidePdfOpenIcon !== undefined ? this.moduleConfig.hidePdfOpenIcon : false;
    },
    shouldPersistFzId() {
      return this.moduleConfig.persistFzId;
    },
    badgeColor() {
      return this.theme.unreadBadgeColor;
    },
    hideFahrzeug() {
      return this.moduleConfig.hideFahrzeug;
    },
    hideBeschrieb() {
      return this.moduleConfig.hideBeschrieb;
    },
    hideErfasser() {
      return this.moduleConfig.hideErfasser;
    },
  },
  methods: {
    itemHasUnreadComments(item) {
      if (item.comments && item.comments.some((c) => !c._userState.dateread)) {
        return true;
      }
      return false;
    },
    openReportDialog(item) {
      if (item.pdf) {
        this.item = item;
        this.reportDialog = true;

        this.addCommentUserState(this.item);
      }
    },
    closeReportDialog() {
      this.reportDialog = false;
    },
    addComment(item) {
      const moduleConfig = this.getModuleConfig('Wagenbuch', this.$route.params.instance);
      const formData = {
        comment: this.comment,
        reportIdFk: item.id,
      };
      this.$store
        .dispatch('wagenbuch/sendComment', {
          instance: this.$route.params.instance,
          module: moduleConfig.title,
          formData,
        })
        .then(() => {
          this.comment = undefined;
          this.commentDialog = false;
        });
    },
    addCommentUserState(item) {
      if (item?.comments) {
        let unreadComments = item.comments.filter((c) => !c._userState.dateread);
        if (unreadComments.length > 0) {
          const moduleConfig = this.getModuleConfig('Wagenbuch', this.$route.params.instance);
          const formData = {
            unreadComments,
          };
          this.$store
            .dispatch('wagenbuch/sendCommentUserState', {
              instance: this.$route.params.instance,
              module: moduleConfig.title,
              formData,
            })
            .then(() => {
              unreadComments.forEach((c) => {
                c._userState.dateread = new Date().toISOString().replace('T', ' ').substring(0, 19);
              });
            });
        }
      }
    },
    getRowClass(item) {
      const color = this.moduleConfig?.statusColors?.[item?.status];
      return color ? `row-color-${color.replace('#', '')}` : 'row-default';
    },
  },
  created() {
    if (this.selectedFzId && this.shouldPersistFzId) {
      if (this.selectedFzId.toString() !== this.$route.params.fzId) {
        this.$router.push({ name: this.$route.name, params: { ...this.$route.params, fzId: this.selectedFzId }, query: { ...this.$route.query } });
      }
    }
  },
  mounted() {
    const colors = this.moduleConfig?.statusColors || {};
    const style = document.createElement('style');

    let css = '';
    for (const [name, hex] of Object.entries(colors)) {
      const className = `.row-color-${hex.replace('#', '')}`;
      css += `
      ${className} {
        background-color: ${hex} !important;
      }
      ${className}:hover {
        opacity: 0.85 !important;
      }
    `;
    }

    style.innerHTML = css;
    document.head.appendChild(style);
  },
  watch: {
    expanded(v) {
      if (v.length > 0) {
        this.singleExpand = true;
      } else {
        this.singleExpand = false;
      }
    },
  },
};
</script>

<style scoped>
/* The ::v-deep was 'deeply' overrides the style of the .v-dialog class */
::v-deep .v-dialog {
  overflow-y: hidden;
}

::v-deep .v-data-table__wrapper {
  overflow-y: auto;
  max-height: calc(100dvh - 190px);
}

::v-deep .v-data-table {
  overflow: visible !important;
}
</style>
